/* tslint:disable:cyclomatic-complexity */
import { observable, Lambda, observe, action, computed, IObservableArray } from "mobx";
import { Parser } from "../../../parser";
import { SelectionModel, ErrorModel, PurchaseModel, UIMessageModel, StakeButtonsStatusesModel, BetModel } from "../../../models";
import { UIMessageStatus, BetStatus, ButtonModifiers, MessageModifiers, StakeButtonsModifiers } from "../../../enums";
import { createTransformer } from "mobx-utils";
import { BaseDict } from "../dict";

/*
    Tests describing the structure of the ouput data should be written first.
    It would be better to split the tests into few suits:
    - base tests related to class and instance structure
    - business logic
    - edge cases
*/

export type ButtonType = {
    label: string;
    handler: (e: React.MouseEvent<HTMLElement>) => void;
    modifier?: ButtonModifiers;
    disabled?: boolean;
};

export type StakeButtonType = {
    modifier: "minus" | "plus";
    handler: (e: React.MouseEvent<HTMLElement>) => void;
    disabled?: boolean;
};

export type StakeType = {
    handler: (str: string) => void;
    buttons: {
        left: StakeButtonType;
        right: StakeButtonType;
    };
};

export class YourbetMiniBetSlipData {
    public provider: Parser;
    private disposeSelections: Lambda;
    private disposeErrors: Lambda;
    private disposePurchases: Lambda;
    private disposeBetStatus: Lambda;
    private disposeStakeButtonsStatus: Lambda;
    private disposePlaceBetButtonState: Lambda;
    private disposeBets: Lambda;

    private dict: BaseDict;
    @observable errors: IObservableArray<ErrorModel>;
    @observable purchases: PurchaseModel[];
    @observable bets: BetModel[]
    @observable selections: SelectionModel[];

    @observable betStatus: number;
    @observable stakeButtonsStatus: StakeButtonsStatusesModel | any;

    @observable isPlaceBetButtonDisabled: boolean;

    constructor(provider: Parser, dict: BaseDict) {
        this.provider = provider;

        this.errors = this.provider.errors as IObservableArray<ErrorModel>;
        this.selections = this.provider.selections;
        this.purchases = this.provider.purchases;
        this.bets = this.provider.bets;
        this.dict = dict;
        this.disposeErrors = () => { };
        this.disposePurchases = () => { };
        this.disposeSelections = () => { };
        this.disposeBetStatus = () => { };
        this.disposeStakeButtonsStatus = () => { };
        this.disposePlaceBetButtonState = () => { };
        this.disposeBets = () => { };

        this.isPlaceBetButtonDisabled = true;
        this.betStatus = this.provider.betStatus;
        this.stakeButtonsStatus = new StakeButtonsStatusesModel({ minusEnabled: false, plusEnabled: true });
    }

    activate() {
        this.disposeErrors = observe(this.provider, "errors", x => {
            this.updateErrors(x.newValue);
        });

        this.disposePurchases = observe(this.provider, "purchases", x => {
            this.updatePurchases(x.newValue);
        });

        this.disposeSelections = observe(this.provider, "selections", x => {
            this.updateSelections(x.newValue);
        });

        this.disposeBetStatus = observe(this.provider, "betStatus", x => {
            this.updateBetStatus(x.newValue);
        });

        this.disposeStakeButtonsStatus = observe(this.provider, "stakeButtonsStatus", x => {
            this.updateStakeButtonsStatus(x.newValue);
        });

        this.disposePlaceBetButtonState = observe(this.provider, "isPlaceBetButtonEnabled", x => {
            this.setIsBetButtonDisabled(!x.newValue);
        });

        this.disposeBets = observe(this.provider, "bets", x => {
            this.updateBets(x.newValue);
        })

        this.provider.activate();
    }

    @action.bound deactivate() {
        this.provider.deactivate();
        this.betStatus = BetStatus.Default;
        this.disposeBetStatus();
        this.disposeErrors();
        this.disposePurchases();
        this.disposeSelections();
        this.disposeStakeButtonsStatus();
        this.disposePlaceBetButtonState();
        this.disposeBets();
    }

    //Testing purposes - delete after finish
    @computed get isWaiting() {
        return this.betStatus === BetStatus.Waiting;
    }

    //Testing purposes - delete after finish
    @computed get isAccepted() {
        return this.betStatus === BetStatus.Accepted;
    }

    @action changeStake(stake: string, buttonAction?: string) {
        return this.provider.changeStake(stake, buttonAction);
    }

    @action.bound updateSelections(updatedSelections: SelectionModel[]) {
        this.selections = updatedSelections;
    }

    @action.bound setIsBetButtonDisabled(value: boolean) {
        this.isPlaceBetButtonDisabled = value;
        return this.isPlaceBetButtonDisabled;
    }

    @action.bound updateErrors(updatedErrors: ErrorModel[]) {
        this.errors = updatedErrors as IObservableArray<ErrorModel>;
    }

    @action.bound updatePurchases(updatedPurchases: PurchaseModel[]) {
        this.purchases = updatedPurchases;
    }

    @action.bound updateBets(updatedBets: BetModel[]) {
        this.bets = updatedBets;
    }

    @action.bound updateStakeButtonsStatus(updatedStatus: StakeButtonsStatusesModel | any) {
        this.stakeButtonsStatus = updatedStatus;
    }

    updateNewOfferStatus(waitingBetId: string, isAccepted: boolean) {
        this.provider.updateNewOfferStatus(waitingBetId, isAccepted);
    }

    updateOddsChangeStatus(isAccepted: boolean) {
        this.provider.updateOddsChangeStatus(isAccepted);
    }

    getBtnRejected() {
        return {
            label: this.dict.Clear,
            handler: this.clearPurchases,
            modifier: ButtonModifiers.clear
        }
    }

    getBtnAccepted() {
        return {
            label: this.dict.Clear,
            handler: this.clearPurchases,
            modifier: ButtonModifiers.clear
        }
    }

    getBtnNewOffer() {
        let stake = this.purchases.length && this.purchases[0] && this.purchases[0].stake;
        let betId = this.bets.length && this.bets[0] && this.bets[0].id || "0";

        return {
            label: stake,
            handler: this.updateNewOfferStatus.bind(this, betId, false),
            modifier: ButtonModifiers.changedStake
        }
    }

    @computed get leftComponentStake(): StakeType {

        let stake = this.purchases.length && this.purchases[0] && this.purchases[0].stake || "0";

        let res = {
            handler: this.changeStake.bind(this),
            buttons: {
                left: {
                    modifier: StakeButtonsModifiers.Minus,
                    handler: this.changeStake.bind(this, stake, StakeButtonsModifiers.Minus),
                    disabled: !this.stakeButtonsStatus.minusEnabled
                },
                right: {
                    modifier: StakeButtonsModifiers.Plus,
                    handler: this.changeStake.bind(this, stake, StakeButtonsModifiers.Plus),
                    disabled: !this.stakeButtonsStatus.plusEnabled
                }
            }
        };

        return res;
    }

    @computed get leftComponentButton(): ButtonType {

        let betStatusKey = BetStatus[this.betStatus];

        let res;
        (typeof this[`getBtn${betStatusKey}`] === "function") ?
            res = this[`getBtn${betStatusKey}`]() :
            res = "";
        return res;
    }

    @computed get rightComponent(): ButtonType {
        let purchase = this.purchases.length && this.purchases[0];
        let bet = this.bets.length && this.bets[0];

        if (this.betStatus === BetStatus.Accepted) {
            return {
                label: this.dict.RetainSelections,
                handler: this.retainPurchases,
                modifier: ButtonModifiers.place,
                disabled: false
            }
        }
        else if (this.betStatus === BetStatus.NewOffer && bet) {
            return {
                label: this.dict.AcceptStake,
                handler: this.updateNewOfferStatus.bind(this, bet.id, true),
                modifier: ButtonModifiers.accepted,
                disabled: false
            }
        }
        else if (purchase && purchase.shouldAcceptNewOdds) {
            return {
                label: this.dict.AcceptOdds,
                handler: this.updateOddsChangeStatus.bind(this, true),
                modifier: ButtonModifiers.accepted,
                disabled: false
            }
        }
        else {

            let placeButtonLabel = this.isPlaceBetButtonDisabled && this.selections.findIndex(s => s.suspended === true) !== -1
                         ? this.dict.Danger
                         : this.dict.PlaceBets;

            return {
                label: placeButtonLabel,
                handler: this.placeBet.bind(this),
                modifier: this.isPlaceBetButtonDisabled ? ButtonModifiers.disabled : ButtonModifiers.place,
                disabled: this.isPlaceBetButtonDisabled
            }
        }
    }

    @action.bound updateBetStatus(updatedBetStatus: number) {
        this.betStatus = updatedBetStatus;
    }

    @action.bound updateBetStatusToRejected() {
        this.updateBetStatus(BetStatus.Rejected);
    }

    @computed get selectionsCount() {
        return this.selections.length;
    }

    @action removeError(key: number, type: string) {
        if (this.errors[key] && this.errors[key][type]) {
            this.errors.remove(this.errors[key]);
        }

        return this.errors;
    }

    formatedErrors() {
        let allErrors: UIMessageModel[] = [];
        this.errors.forEach((error, index) => (
            Object.keys(error).forEach(x => {
                let formatedError = new UIMessageModel({
                    id: `error-${x}`,
                    label: error[x].join(" "),
                    modifier: MessageModifiers.Change,
                    type: UIMessageStatus.Error,
                    handler: this.removeError.bind(this, index, x)
                });

                allErrors.push(formatedError);
            }, this)
        ), this);

        return allErrors;
    }

    formatedAcceptedMessage(purchaseId?: string) {
        return new UIMessageModel({
            id: purchaseId ? `accepted-${purchaseId}` : `accepted`,
            modifier: MessageModifiers.Accepted,
            label: this.dict.BetsAccepted,
            type: UIMessageStatus.Accepted
        });
    }

    formatedOddsChangeMessage(purchaseId?: string) {
        return new UIMessageModel({
            id: purchaseId ? `oddsChanged-${purchaseId}` : `oddsChanged`,
            modifier: MessageModifiers.Change,
            label: this.dict.OddsChanged,
            type: UIMessageStatus.Default
        });
    }

    formatedStakeChangeMessage(purchaseId?: string) {
        return new UIMessageModel({
            id: purchaseId ? `stakeChanged-${purchaseId}` : `stakeChanged`,
            modifier: MessageModifiers.Change,
            label: this.dict.StakeChanged,
            type: UIMessageStatus.Waiting
        });
    }

    formatedTaxMessage(taxText: string, purchaseId?: string) {
        return new UIMessageModel({
            id: purchaseId ? `tax-${purchaseId}` : `tax`,
            modifier: MessageModifiers.Tax,
            label: taxText,
            type: UIMessageStatus.Tax
        });
    }

    formatedPurchaseMessage(text: string, purchaseId?: string) {
        return new UIMessageModel({
            id: purchaseId ? `purchase-msg-${purchaseId}` : `purchase-msg`,
            modifier: MessageModifiers.Purchase,
            label: text,
            type: UIMessageStatus.Purchase
        });
    }

    shouldDisplayError() {
        return (this.betStatus === BetStatus.Rejected || this.betStatus === BetStatus.Default) && this.errors.length !== 0 && this.selections.length > 0;
    }

    getTax(purchaseId: string) {
        let currentPurchase = this.purchases.find(x => x.id === purchaseId);
        return currentPurchase && currentPurchase.tax || "";
    }

    getPurchaseMessages(purchaseId: string) {
        let currentMessages: UIMessageModel[] = [];
        let currentPurchase = this.purchases.find(x => x.id === purchaseId);
        currentPurchase && currentPurchase.messages && currentPurchase.messages.forEach((msg) => {
            msg !== "" && currentMessages.push(this.formatedPurchaseMessage(msg, purchaseId));
        }, this);

        return currentMessages;
    }

    messages = createTransformer((purchaseId: string) => {

        let allMessages: UIMessageModel[] = [];
        let tax = this.getTax(purchaseId);

        if (this.shouldDisplayError()) {
            allMessages = allMessages.concat(this.formatedErrors());
        }
        this.betStatus === BetStatus.NewOffer && allMessages.push(this.formatedStakeChangeMessage(purchaseId));
        this.betStatus === BetStatus.Accepted && allMessages.push(this.formatedAcceptedMessage(purchaseId));
        let currentPurchase = this.purchases.find(x => x.id === purchaseId);

        if (currentPurchase && currentPurchase.shouldAcceptNewOdds) {
            allMessages.push(this.formatedOddsChangeMessage(purchaseId))
        }

        tax !== "" && allMessages.push(this.formatedTaxMessage(tax));

        if (this.isBetWaitingOrAccepted) {
            allMessages = allMessages.concat(this.getPurchaseMessages(purchaseId));
        }
        return allMessages;
    });

    placeBet() {
        let result: any = false;
        this.purchases.forEach(p => {
            if (p && p.stake) {
                result = this.provider.placeBet(p.stake, this.selections);
            }
        }, this);

        return result;
    }

    removeSelection(selectionId: string) {
        let result: any = false;
        let selection = this.selections.find(sel => sel.id === selectionId);
        if (selection) {
            result = this.provider.removeSelections([selection]);
        }
        return result;
    }

    @action.bound retainPurchases() {
        let result: any = false;
        this.purchases.forEach(p => {
            if (p.id && p.viewKey) {
                result = this.provider.retainPurchase(p.id, p.viewKey);
            }
        }, this);
        return result;
    }

    @action.bound clearPurchases() {
        let result: any = false;

        let purchases = this.purchases.filter(x => x.id !== "" && x.viewKey !== undefined);
        if (purchases.length > 0) {
            result = this.provider.clearAllPurchases(purchases);
        }

        return result;
    }

    @computed get isBetWaitingOrAccepted() {
        return this.betStatus === BetStatus.Waiting || this.betStatus === BetStatus.Accepted;
    }

    @computed get stakeValue() {
        return this.purchases.length && this.purchases[0].stake || "0";
    }
}
/* tslint:enable:cyclomatic-complexity */